import { consume } from '@lit/context';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { create as createJoystick } from 'nipplejs';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Touch Controls — on-screen joystick + action-button cluster for mobile.
 *
 * Owns the nipplejs joystick (rendered into its own shadow root — nipplejs v1
 * appends its UI into the zone element and styles it inline, so shadow DOM is
 * fine) and forwards raw gestures to the game through GameAPI. The angle→key
 * translation and its timing live game-side in `Input.ts` (unchanged), so the
 * flight behaviour matches the legacy controls.
 *
 * The joystick runs in `dynamic` mode (spawns under the thumb) rather than the
 * legacy `static` stick — a pure ergonomic upgrade to the same point-to-aim
 * scheme; the game-side timing/network path is untouched.
 *
 * The bottom-right cluster holds four round, icon'd buttons on a thumb arc:
 * FIRE + SPECIAL (the big sweet-spot pair, on a `/` diagonal), DROP (small, set
 * up and clear of the fire-mash zone — a mis-tap drops your flag in CTF), and
 * CRUISE (small toggle; grey idle, green when active). Icons are mask-recoloured
 * Lucide glyphs (same mechanism as `cruise-selector`). On mobile this cluster is
 * the only cruise control — `airmash-game` drops the left `cruise-selector` on
 * mobile so it doesn't double up.
 *
 * Mounted by `airmash-game`, gated on `uiState.isMobile`; auto-hides (and
 * releases held keys) while a menu/panel is open or the player is spectating.
 *
 * @element touch-controls
 */
@customElement('touch-controls')
export class TouchControls extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Full-viewport overlay that does not capture touches itself — only the
         joystick zone and the action buttons do (pointer-events: auto below),
         so taps on empty space still reach the game canvas behind the UI. */
      :host {
        position: absolute;
        inset: 0;
        /* Above the notice tier (kill and death notices 50, the match results
           and level-up 100), so the buttons stay visible over them, as over
           the powerup bar. This is for sight: those are all click-through, so
           presses reach the buttons either way. Below the toolbars (150) and
           everything above them. */
        z-index: 110;
        pointer-events: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Hidden while a menu/panel is open (toggled in willUpdate). */
      :host([hidden]) {
        display: none;
      }

      .joystick-zone {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 200px;
        height: 200px;
        pointer-events: auto;
        touch-action: none;
      }

      /* --- Action-button cluster (bottom-right thumb arc) -------------------
         FIRE + SPECIAL are the big sweet-spot pair on a "/" diagonal — FIRE
         lifted OFF the dead bottom-right corner to the thumb's natural rest
         (lower-centre), SPECIAL up-and-right of it along the thumb's arc. The
         corner itself over-flexes the thumb (cramped), so nothing sits there.
         DROP and CRUISE are smaller, set up / out of the fire-mash zone — DROP
         the most isolated, since a mis-tap drops your flag in CTF. Each button
         is a round tap target; the icon is a mask-recoloured Lucide glyph drawn
         in ::after (same mechanism as cruise-selector). Geometry is
         portrait-first and tuned on-device; landscape gets its own pass.

         The buttons live in a full-viewport .cluster wrapper so they can be
         scaled as a group on very short landscape screens (see below). inset:0
         keeps each button's right/bottom offsets measured from the viewport edges
         (positions unchanged); pointer-events:none lets the joystick zone and
         empty space through. */
      .cluster {
        position: absolute;
        inset: 0;
        pointer-events: none;
        transform-origin: bottom right;
      }

      .btn {
        position: absolute;
        pointer-events: auto;
        touch-action: none;
        border-radius: 50%;
        background-color: var(--color-overlay-medium);
        -webkit-tap-highlight-color: transparent;
      }

      .btn::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: var(--color-text-white);
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      /* Pressed feedback for the momentary buttons (FIRE/SPECIAL/DROP): brighten
         the disc while held. No 0.5 white-overlay token exists yet. */
      .btn.pressed {
        background-color: rgba(255, 255, 255, 0.5);
      }

      .btn.fire {
        right: 84px;
        bottom: 36px;
        width: 96px;
        height: 96px;
      }
      .btn.fire::after {
        mask-image: url('/vanilla/ui/menu/crosshair.svg');
        mask-size: 40px;
        -webkit-mask-image: url('/vanilla/ui/menu/crosshair.svg');
        -webkit-mask-size: 40px;
      }

      .btn.special {
        right: 67px;
        bottom: 148px;
        width: 78px;
        height: 78px;
      }
      .btn.special::after {
        mask-image: url('/vanilla/ui/menu/zap.svg');
        mask-size: 36px;
        -webkit-mask-image: url('/vanilla/ui/menu/zap.svg');
        -webkit-mask-size: 36px;
      }

      .btn.drop {
        right: 106px;
        bottom: 231px;
        width: 54px;
        height: 54px;
      }
      .btn.drop::after {
        mask-image: url('/vanilla/ui/menu/arrow-down-to-line.svg');
        mask-size: 26px;
        -webkit-mask-image: url('/vanilla/ui/menu/arrow-down-to-line.svg');
        -webkit-mask-size: 26px;
      }

      /* DROP is contextual: dead (greyed, taps ignored) unless there's something
         to drop — an upgrade in FFA/BTR, or the flag you're carrying in CTF. */
      .btn.drop.disabled {
        opacity: 0.35;
      }

      /* CRUISE — a toggle, not a momentary press: grey icon idle, neon green
         when cruise mode is on (mirrors the desktop cruise-selector). */
      .btn.cruise {
        right: 155px;
        bottom: 168px;
        width: 54px;
        height: 54px;
      }
      .btn.cruise::after {
        background-color: var(--color-text-tertiary);
        mask-image: url('/vanilla/ui/menu/circle-gauge.svg');
        mask-size: 26px;
        -webkit-mask-image: url('/vanilla/ui/menu/circle-gauge.svg');
        -webkit-mask-size: 26px;
      }
      .btn.cruise.active {
        background-color: var(--color-overlay-strong);
      }
      .btn.cruise.active::after {
        background-color: var(--color-cruise-active);
      }

      /* Portrait: a roomy bottom-left zone so the dynamic stick spawns under a
         comfortably-placed thumb (overrides the compact size above, for portrait
         of any width). Inset from the left edge — clear of the bottom-right
         cluster — and capped at 45vh so it stays below the top-left kill-feed. It
         may overlap the centred minimap's left edge near the bottom; the minimap
         is display-only on mobile and renders behind the canvas, so that's
         harmless.

         The left inset matters: the dynamic stick spawns at the touch point, so a
         stick spawned flush against x=0 has no room to drag left and can't steer
         left at all. The inset guarantees clearance for a leftward swing.
         Starting point — tune to taste (portrait-first per the controls rework). */
      @media (orientation: portrait) {
        .joystick-zone {
          left: 32px;
          width: 50vw;
          height: 45vh;
        }
      }

      /* Landscape (the primary play mode). Two-handed grip: left thumb on the
         stick, right thumb on the cluster, both pivoting from the bottom corners
         of a wide, short viewport. The zone keeps the portrait edge fixes — a
         left inset (a stick flush at x=0 can't drag left) and a bottom inset (one
         flush at the bottom can't drag down) — sized in vw/vh for the short
         screen. The cluster is compressed vertically vs portrait, since the short
         height gives the thumb less vertical arc. FIRST CUT — tuned on-device. */
      @media (orientation: landscape) {
        .joystick-zone {
          left: 32px;
          bottom: 32px;
          width: 30vw;
          height: 60vh;
        }

        .btn.fire {
          right: 132px;
          bottom: 28px;
        }
        .btn.special {
          right: 113px;
          bottom: 126px;
        }
        .btn.drop {
          right: 154px;
          bottom: 204px;
        }
        /* CRUISE shows on both schemes (point-to-aim too, since 055fc07). Placed
           inner-left of the landscape stack, mid-height — clear of
           fire/special/drop. FIRST CUT, tune on-device alongside the rest of the
           landscape cluster. */
        .btn.cruise {
          right: 200px;
          bottom: 146px;
        }
      }

      /* Safety net for very short landscape screens (tiny / old phones): scale
         the whole cluster down toward the bottom-right corner so its top (DROP)
         can't climb into the top-right HUD (menu / ping / volume). Modern phones
         (~390px+ tall in landscape) never trip this; it only bites ~360px and
         below. Scaling the group keeps the layout + icons intact, DROP stays
         ≥44px, and the joystick zone (outside the wrapper) is unaffected. */
      @media (orientation: landscape) and (max-height: 360px) {
        .cluster {
          transform: scale(0.82);
        }
      }
    `,
  ];

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  @query('.joystick-zone')
  private zoneEl!: HTMLElement;

  /** nipplejs joystick. Typed via ReturnType — v1 doesn't export its Collection type. */
  private joystick: ReturnType<typeof createJoystick> | null = null;

  @state() private firePressed = false;
  @state() private specialPressed = false;
  @state() private dropPressed = false;

  private uiState = bindValtio(this, uiStateProxy);

  /**
   * Whether DROP would do anything right now: in CTF you must be carrying a flag;
   * otherwise (FFA/BTR) you must have an upgrade banked. Drives the button's
   * dead/greyed state and short-circuits the tap. Reads reactive UIState mirrors.
   */
  private get canDrop(): boolean {
    return this.uiState.gameType === 'ctf' ? this.uiState.isFlagCarrier : this.uiState.availableUpgrades > 0;
  }

  override willUpdate() {
    // Hide the controls — and release any held keys — whenever a menu/panel is
    // open OR the player is dead/spectating. In both cases the joystick and
    // buttons can't fly anything, so they'd only float over the menu / spectator
    // UI (covering it) or keep driving a ship that no longer exists. Releasing on
    // hide also stops a movement key sticking if you die mid-drag.
    const shouldHide =
      this.uiState.activePanel !== null ||
      this.uiState.openTopBarPanel !== null ||
      this.uiState.spectator !== null ||
      this.uiState.chat.mobileDrawerOpen;
    if (shouldHide !== this.hidden) {
      this.hidden = shouldHide;
      if (shouldHide) {
        this.releaseAll();
      }
    }
  }

  override firstUpdated() {
    // Dynamic joystick: the stick spawns under the thumb wherever you press
    // inside the zone (vs the legacy static stick pinned to the zone centre),
    // so there's nothing to reach for. Same point-to-aim scheme — only the
    // ergonomics change; the game-side timing/network path is untouched.
    // nipplejs defaults to multitouch:false, which caps the zone at one
    // joystick, so a stray second touch can't spawn a rival stick. `position`
    // is static-only (the spawn point comes from the touch), so it's omitted.
    this.joystick = createJoystick({
      zone: this.zoneEl,
      mode: 'dynamic',
      // Subtle, additive circles to match the action buttons: 50% base + 50%
      // nipple, brighter where they overlap at centre. nipplejs bakes
      // opacity:0.5 onto the front nipple, so an opaque `front` color renders at
      // 50%; `back` has no own opacity so it carries its 0.5 in the color. (In
      // dynamic mode nipplejs forces restOpacity to 0 — the stick fades fully out
      // on release — so the alpha must live here, not in restOpacity.)
      color: { front: 'rgb(255, 255, 255)', back: 'rgba(255, 255, 255, 0.5)' },
    });
    // 'move' fires on every pointermove (nipplejs 1.0.4 Collection.processOnMove),
    // matching the legacy steering cadence; the throttle lives in handleRotation.
    this.joystick.on('move', (evt) => {
      this.gameApi.touchJoystickMove(evt.data.angle.radian, evt.data.force);
    });
    this.joystick.on('end', () => {
      this.gameApi.touchJoystickEnd();
    });

    // Suppress the iOS long-press magnifier on the joystick zone — the same
    // gesture fix the canvas and the action buttons use (those preventDefault in
    // their own touchstart handlers). nipplejs steers via pointer events, which
    // fire independently of this touch handler, so the stick is unaffected.
    // Passive must be false so preventDefault is honored.
    this.zoneEl.addEventListener('touchstart', (event) => event.preventDefault(), { passive: false });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.joystick?.destroy();
    this.joystick = null;
  }

  private readonly onFireStart = (e: TouchEvent) => {
    e.preventDefault();
    this.firePressed = true;
    this.gameApi.touchButton('FIRE', true);
  };

  private readonly onFireEnd = (e: TouchEvent) => {
    e.preventDefault();
    this.firePressed = false;
    this.gameApi.touchButton('FIRE', false);
  };

  private readonly onSpecialStart = (e: TouchEvent) => {
    e.preventDefault();
    this.specialPressed = true;
    this.gameApi.touchButton('SPECIAL', true);
  };

  private readonly onSpecialEnd = (e: TouchEvent) => {
    e.preventDefault();
    this.specialPressed = false;
    this.gameApi.touchButton('SPECIAL', false);
  };

  private readonly onDropStart = (e: TouchEvent) => {
    e.preventDefault(); // kill the iOS magnifier even when the button is dead
    if (!this.canDrop) return;
    this.dropPressed = true;
    // One-shot: the contextual drop (flag in CTF, powerup otherwise) fires on
    // press and lives game-side; the release handler just clears the visual.
    this.gameApi.touchAction('DROP');
  };

  private readonly onDropEnd = (e: TouchEvent) => {
    e.preventDefault();
    this.dropPressed = false;
  };

  // Cruise toggle — touchstart (not click) so preventDefault can kill the iOS
  // magnifier; the active (green) state tracks uiState.cruiseMode reactively.
  private readonly onCruiseToggle = (e: TouchEvent) => {
    e.preventDefault();
    this.gameApi.setCruiseMode(!this.uiState.cruiseMode);
  };

  /** Release every held key AND drop the pressed look, together. Used for
   *  touchcancel and for hiding: hiding once released only the keys, so a thumb
   *  still on FIRE when the controls came back (chat drawer closed, or respawn
   *  after dying mid-hold) showed FIRE lit while the plane wasn't firing. A
   *  fresh press re-asserts it; the old touch's eventual touchend just repeats a
   *  release. (Setting @state here from willUpdate folds into the current
   *  update — no extra render.) */
  private releaseAll(): void {
    this.firePressed = false;
    this.specialPressed = false;
    this.dropPressed = false;
    this.gameApi.touchCancelRelease();
  }

  /** touchcancel safety — mirrors the legacy global handler: release everything. */
  private readonly onCancel = () => {
    this.releaseAll();
  };

  override render() {
    return html`
      <div class="joystick-zone"></div>
      <div class="cluster">
        <div
          class="btn fire ${this.firePressed ? 'pressed' : ''}"
          @touchstart=${this.onFireStart}
          @touchend=${this.onFireEnd}
          @touchcancel=${this.onCancel}
        ></div>
        <div
          class="btn special ${this.specialPressed ? 'pressed' : ''}"
          @touchstart=${this.onSpecialStart}
          @touchend=${this.onSpecialEnd}
          @touchcancel=${this.onCancel}
        ></div>
        <div
          class="btn drop ${this.dropPressed ? 'pressed' : ''} ${this.canDrop ? '' : 'disabled'}"
          @touchstart=${this.onDropStart}
          @touchend=${this.onDropEnd}
          @touchcancel=${this.onCancel}
        ></div>
        <div class="btn cruise ${this.uiState.cruiseMode ? 'active' : ''}" @touchstart=${this.onCruiseToggle}></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'touch-controls': TouchControls;
  }
}
